import Layout from "@/components/Layout";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ReportingToPicker, employeeReportingLabel } from "@/components/hr/ReportingToPicker";
import { formatTimeDisplayAmPm } from "@/components/hr/TimePickerField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getStoredAuthUser } from "@/lib/authUser";
import { formatWithMask } from "@/lib/utils";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import { listDesignations, syncDesignationsFromApi } from "@/lib/hr/designationsStore";
import {
  getEmployeeAllowanceTypeById,
  listEmployeeAllowanceTypeRecords,
  syncEmployeeAllowanceTypesFromApi,
} from "@/lib/hr/employeeAllowanceTypesStore";
import {
  getEmployeeDeductionTypeById,
  listEmployeeDeductionTypeRecords,
  syncEmployeeDeductionTypesFromApi,
} from "@/lib/hr/employeeDeductionTypesStore";
import { listLeavePolicies, syncLeavePoliciesFromApi } from "@/lib/hr/leavePoliciesStore";
import { getActiveLeaveYear, syncLeaveYearsFromApi } from "@/lib/hr/leaveYearsStore";
import { fetchAssignableWorkPoliciesForEmployees } from "@/lib/hr/workPoliciesStore";
import { fetchAssignableSalaryPoliciesForEmployees } from "@/lib/hr/salaryPoliciesStore";
import { listWorkShifts, syncWorkShiftsFromApi } from "@/lib/hr/workShiftsStore";
import {
  createEmployee,
  EMPLOYEE_OFF_DAY_OPTIONS,
  employeeApiErrorMessage,
  emptyEmployeeDraft,
  getEmployeeById,
  listEmployees,
  recordToDraft,
  syncEmployeesFromApi,
  suggestNextEmployeeCode,
  toggleEmployeeOffDay,
  updateEmployee,
  updateEmployeeCode,
  type EmployeeAllowance,
  type EmployeeDeduction,
  type EmployeeDraft,
} from "@/lib/hr/employeesStore";
import { cn } from "@/lib/utils";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { format, isValid, parseISO } from "date-fns";
import {
  Accessibility,
  ArrowLeft,
  Building2,
  Banknote,
  FileCheck2,
  CalendarIcon,
  Camera,
  Check,
  Loader2,
  Pencil,
  Plus,
  Save,
  ScrollText,
  Trash2,
  Upload,
  User,
  Wallet,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SpatieAccessEditor } from "@/components/admin/SpatieAccessEditor";
import {
  listManagedPermissions,
  syncManagedPermissionsFromApi,
  type ManagedPermissionRecord,
} from "@/lib/admin/managedPermissionsStore";
import { listRoles, syncRolesFromApi, type RoleRecord } from "@/lib/admin/rolesStore";
import { fetchUserAccessDetail } from "@/lib/admin/userAccessStore";

const EMPLOYEE_TYPES = ["Permanent", "Contract", "Probation", "Temporary"] as const;
const EMPLOYEE_STATUSES = ["Active", "Inactive"] as const;

/** Country dropdown is Pakistan-only for now. */
const PAKISTAN_ONLY = "Pakistan" as const;

/** Pakistan provinces and administrative areas (English names). */
const PAKISTAN_PROVINCES = [
  "Punjab",
  "Sindh",
  "Khyber Pakhtunkhwa",
  "Balochistan",
  "Islamabad Capital Territory",
  "Gilgit-Baltistan",
  "Azad Jammu and Kashmir",
] as const;

const PROVINCE_SELECT_NONE = "__province_none__";

/** Radix Select sentinel when country is cleared (dropdown is Pakistan-only for now). */
const COUNTRY_SELECT_NONE = "__country_none__";

/** Select sentinel when no allowance type is chosen (Radix Select cannot use empty string as value). */
const ALLOWANCE_TYPE_NONE = "__allowance_type_none__";
/** Select sentinel when no deduction type is chosen (Radix Select cannot use empty string as value). */
const DEDUCTION_TYPE_NONE = "__deduction_type_none__";

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
}) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
    </div>
  </div>
);

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 pt-2 border-t border-border/40 first:border-t-0 first:pt-0">
      <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}

function PakistanCountrySelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const trimmed = value.trim();
  const orphan = trimmed && trimmed !== PAKISTAN_ONLY ? trimmed : null;
  const selectValue = orphan ?? (!trimmed ? COUNTRY_SELECT_NONE : PAKISTAN_ONLY);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === COUNTRY_SELECT_NONE) onChange("");
          else onChange(v);
        }}
      >
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Country" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={COUNTRY_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          <SelectItem value={PAKISTAN_ONLY}>{PAKISTAN_ONLY}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function PakistanProvinceSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const trimmed = value.trim();
  const provinces = PAKISTAN_PROVINCES as readonly string[];
  const inList = Boolean(trimmed && provinces.includes(trimmed));
  const orphan = trimmed && !inList ? trimmed : null;
  const selectValue = orphan ?? (inList ? trimmed : PROVINCE_SELECT_NONE);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={selectValue} onValueChange={(v) => onChange(v === PROVINCE_SELECT_NONE ? "" : v)}>
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Select province" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={PROVINCE_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          {PAKISTAN_PROVINCES.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function parseDate(iso: string): Date | undefined {
  if (!iso.trim()) return undefined;
  const d = parseISO(iso);
  return isValid(d) ? d : undefined;
}

function toIso(d: Date | undefined): string {
  if (!d || !isValid(d)) return "";
  return format(d, "yyyy-MM-dd");
}

function displayDate(iso: string): string {
  if (!iso.trim()) return "Pick date";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "dd/MM/yyyy") : iso;
}

function formatGrossDisplay(stored: string): string {
  const digits = stored.replace(/[^\d]/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-US");
}

function grossInvalid(gross: string): boolean {
  const t = gross.trim().replace(/,/g, "");
  if (!t) return false;
  return Number.isNaN(Number(t));
}

function allowanceAmountInvalid(amount: string): boolean {
  const t = amount.trim().replace(/,/g, "");
  if (!t) return false;
  return Number.isNaN(Number(t));
}

const EMPLOYEE_PHOTO_MAX_BYTES = 5 * 1024 * 1024;

function readPhotoAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const AddEmployee = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const user = useMemo(() => getStoredAuthUser(), []);
  const canEdit = isEdit ? can(user, "employee_can_update") : can(user, "employee_can_create");

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [wizardStep, setWizardStep] = useState(0);
  const [draft, setDraft] = useState<EmployeeDraft>(() =>
    emptyEmployeeDraft({
      employeeType: "Permanent",
      employeeStatus: "Active",
    }),
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [editHydrated, setEditHydrated] = useState(() => !editId);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const [employeePhotoPreview, setEmployeePhotoPreview] = useState<string | null>(null);
  const [employeePhotoFile, setEmployeePhotoFile] = useState<File | null>(null);
  /** Synced as soon as a file passes validation so submit always has the blob (upload or camera). */
  const employeePhotoFileRef = useRef<File | null>(null);
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const photoUploadInputRef = useRef<HTMLInputElement>(null);
  const captureVideoRef = useRef<HTMLVideoElement>(null);
  const captureStreamRef = useRef<MediaStream | null>(null);
  const [assignableWorkPolicies, setAssignableWorkPolicies] = useState<Array<{ id: string; name: string }>>([]);
  const [assignableSalaryPolicies, setAssignableSalaryPolicies] = useState<Array<{ id: string; name: string }>>([]);
  const [employeeCodeModalOpen, setEmployeeCodeModalOpen] = useState(false);
  const [employeeCodeDraft, setEmployeeCodeDraft] = useState("");
  const [employeeCodeSaving, setEmployeeCodeSaving] = useState(false);

  const showAccessibilityStep = isEdit && Boolean(draft.userId?.trim());

  const wizardSteps = useMemo(
    () =>
      showAccessibilityStep
        ? [
            { id: "basic" as const, label: "Basic Information" },
            { id: "employment" as const, label: "Employment Information" },
            { id: "accessibility" as const, label: "Accessibility" },
            { id: "salary" as const, label: "Salary Information" },
          ]
        : [
            { id: "basic" as const, label: "Basic Information" },
            { id: "employment" as const, label: "Employment Information" },
            { id: "salary" as const, label: "Salary Information" },
          ],
    [showAccessibilityStep],
  );

  const stepCount = wizardSteps.length;
  const currentStepId = wizardSteps[wizardStep]?.id ?? "basic";

  const [accessCatalogLoading, setAccessCatalogLoading] = useState(false);
  const [accessReady, setAccessReady] = useState(false);
  const [roleCatalog, setRoleCatalog] = useState<RoleRecord[]>([]);
  const [permCatalog, setPermCatalog] = useState<ManagedPermissionRecord[]>([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState<Set<string>>(() => new Set());
  const [selectedPermNames, setSelectedPermNames] = useState<Set<string>>(() => new Set());

  const departments = useMemo(() => listDepartments(), [version]);
  const designations = useMemo(() => listDesignations(), [version]);
  const allowanceTypes = useMemo(() => listEmployeeAllowanceTypeRecords(), [version]);
  const deductionTypes = useMemo(() => listEmployeeDeductionTypeRecords(), [version]);
  const leavePolicies = useMemo(() => listLeavePolicies(), [version]);
  const activeLeaveYear = useMemo(() => getActiveLeaveYear(), [version]);
  const workShifts = useMemo(() => listWorkShifts(), [version]);
  const employees = useMemo(() => listEmployees(), [version]);

  const mask = user?.company_branding?.cnic_masking;
  const placeholder = mask ? mask.replace(/9/g, "x") : "National ID / CNIC";
  const maxLength = mask ? mask.length : 32;

  const reportingToOptions = useMemo(() => {
    const selfId = editId ? String(editId) : "";
    return employees.filter((e) => String(e.id) !== selfId);
  }, [employees, editId]);

  const reportingToOrphan = useMemo(() => {
    const id = draft.reportingTo.trim();
    if (!id || reportingToOptions.some((e) => String(e.id) === id)) return null;
    const hit = getEmployeeById(id);
    return hit ? `${employeeReportingLabel(hit)} (not in list)` : `Employee #${id} (not in list)`;
  }, [draft.reportingTo, reportingToOptions]);

  const selectedWorkShift = useMemo(() => {
    const id = (draft.workShiftId ?? "").trim();
    if (!id) return null;
    const hit = workShifts.find((s) => String(s.id) === id) ?? null;
    if (hit) return hit;
    const current = draft.workShiftAssignments?.find((a) => !a.effectiveTo) ?? null;
    if (current && current.workShiftId === id) {
      return {
        id: current.workShiftId,
        name: current.workShiftName,
        slug: current.workShiftSlug,
        startTime: current.startTime,
        endTime: current.endTime,
        breakMinutes: current.breakMinutes,
        crossesMidnight: current.crossesMidnight,
        active: current.active,
        createdAt: "",
        updatedAt: "",
      };
    }
    return null;
  }, [draft.workShiftId, draft.workShiftAssignments, workShifts]);

  const workPolicySelectOptions = useMemo(() => {
    const out = assignableWorkPolicies.map((p) => ({ ...p }));
    const wid = (draft.workPolicyId ?? "").trim();
    if (wid && !out.some((p) => String(p.id) === wid)) {
      const nm = draft.workPolicyName?.trim();
      out.unshift({
        id: wid,
        name: nm ? `${nm} (not assignable — change or clear)` : `Work policy #${wid} (not assignable)`,
      });
    }
    return out.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [assignableWorkPolicies, draft.workPolicyId, draft.workPolicyName]);

  const salaryPolicySelectOptions = useMemo(() => {
    const out = assignableSalaryPolicies.map((p) => ({ ...p }));
    const sid = (draft.salaryPolicyId ?? "").trim();
    if (sid && !out.some((p) => String(p.id) === sid)) {
      const nm = draft.salaryPolicyName?.trim();
      out.unshift({
        id: sid,
        name: nm ? `${nm} (not assignable — change or clear)` : `Salary policy #${sid} (not assignable)`,
      });
    }
    return out.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [assignableSalaryPolicies, draft.salaryPolicyId, draft.salaryPolicyName]);

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      setBootLoading(true);
      try {
        if (!canEdit) {
          if (mounted) toast.error("User does not have the right permissions.");
          navigate("/dashboard", { replace: true });
          return;
        }
        await Promise.all([
          syncEmployeesFromApi(),
          syncDepartmentsFromApi(),
          syncDesignationsFromApi(),
          syncEmployeeAllowanceTypesFromApi(),
          syncEmployeeDeductionTypesFromApi(),
          syncLeavePoliciesFromApi(),
          syncLeaveYearsFromApi(),
          syncWorkShiftsFromApi(),
        ]);
      } catch {
        /* non-fatal */
      }
      try {
        const assignable = await fetchAssignableWorkPoliciesForEmployees();
        if (mounted) setAssignableWorkPolicies(assignable);
      } catch {
        if (mounted) setAssignableWorkPolicies([]);
      }
      try {
        const assignableSalary = await fetchAssignableSalaryPoliciesForEmployees();
        if (mounted) setAssignableSalaryPolicies(assignableSalary);
      } catch {
        if (mounted) setAssignableSalaryPolicies([]);
      }
      if (mounted) refresh();
      if (mounted) setBootLoading(false);
    };
    void boot();
    return () => {
      mounted = false;
    };
  }, [refresh, canEdit, navigate]);

  useEffect(() => {
    if (editId) {
      setEditHydrated(false);
      setEditLoadError(null);
      setWizardStep(0);
      setAccessReady(false);
      setRoleCatalog([]);
      setPermCatalog([]);
      setSelectedRoleNames(new Set());
      setSelectedPermNames(new Set());
      setEmployeePhotoPreview(null);
      setEmployeePhotoFile(null);
      employeePhotoFileRef.current = null;
      return;
    }
    setEditHydrated(true);
    setEditLoadError(null);
    setWizardStep(0);
    setDraft(
      emptyEmployeeDraft({
        employeeType: "Permanent",
        employeeStatus: "Active",
        leaveYearId: activeLeaveYear?.id ? String(activeLeaveYear.id) : "",
      }),
    );
    setEmployeePhotoPreview(null);
    setEmployeePhotoFile(null);
    employeePhotoFileRef.current = null;
  }, [editId]);

  useEffect(() => {
    if (!editId) return;
    if (bootLoading) return;
    const rec = getEmployeeById(editId);
    if (!rec) {
      setEditLoadError("Employee not found.");
      setEditHydrated(true);
      return;
    }
    setEditLoadError(null);
    setDraft(recordToDraft(rec));
    setEmployeePhotoPreview(rec.photoDataUrl?.trim() ? rec.photoDataUrl : null);
    setEmployeePhotoFile(null);
    employeePhotoFileRef.current = null;
    setEditHydrated(true);
  }, [editId, bootLoading]);

  useEffect(() => {
    setWizardStep((s) => Math.min(s, Math.max(0, wizardSteps.length - 1)));
  }, [wizardSteps.length]);

  useEffect(() => {
    if (!isEdit || !editHydrated || !draft.userId?.trim()) {
      setAccessCatalogLoading(false);
      if (!draft.userId?.trim()) setAccessReady(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setAccessCatalogLoading(true);
      try {
        await Promise.all([syncRolesFromApi(), syncManagedPermissionsFromApi()]);
        if (cancelled) return;
        setRoleCatalog(listRoles());
        const allPerms = listManagedPermissions();
        const authUser = getStoredAuthUser();
        const accountRole = (authUser?.role ?? "").toLowerCase().trim();
        const isCompanyOwner = accountRole === "company-owner" || accountRole === "company owner";
        if (isCompanyOwner) {
          const allowed = new Set((authUser?.permissions ?? []).map((p) => String(p)));
          setPermCatalog(allPerms.filter((p) => allowed.has(p.name)));
        } else {
          setPermCatalog(allPerms);
        }
        const detail = await fetchUserAccessDetail(draft.userId.trim());
        if (cancelled) return;
        setSelectedRoleNames(new Set(detail.roles.map((r) => r.name)));
        setSelectedPermNames(new Set(detail.directPermissions.map((p) => p.name)));
        setAccessReady(true);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error("Could not load roles and permissions for this account.");
          setAccessReady(false);
        }
      } finally {
        if (!cancelled) setAccessCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editHydrated, draft.userId]);

  useEffect(() => {
    if (editId) return;
    const employees = listEmployees();
    const code = suggestNextEmployeeCode(employees);
    setDraft((d) => (d.employeeCode ? d : { ...d, employeeCode: code }));
  }, [version, editId]);

  useEffect(() => {
    if (isEdit) return;
    const activeId = activeLeaveYear?.id ? String(activeLeaveYear.id) : "";
    if (!activeId) return;
    setDraft((d) => (d.leaveYearId ? d : { ...d, leaveYearId: activeId }));
  }, [activeLeaveYear?.id, isEdit]);

  const update = <K extends keyof EmployeeDraft>(key: K, value: EmployeeDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const processEmployeePhotoFile = async (file: File | undefined) => {
    if (!file) return;
    const okType = /^image\/(jpeg|png)$/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name);
    if (!okType) {
      toast.error("Please choose a JPG or PNG image.");
      return;
    }
    if (file.size > EMPLOYEE_PHOTO_MAX_BYTES) {
      toast.error("Photo must be 5MB or smaller.");
      return;
    }
    employeePhotoFileRef.current = file;
    try {
      const url = await readPhotoAsDataUrl(file);
      setEmployeePhotoPreview(url);
      setEmployeePhotoFile(file);
    } catch {
      employeePhotoFileRef.current = null;
      toast.error("Could not read the photo. Try another file.");
    }
  };

  const clearEmployeePhoto = () => {
    setEmployeePhotoPreview(null);
    setEmployeePhotoFile(null);
    employeePhotoFileRef.current = null;
    if (photoUploadInputRef.current) photoUploadInputRef.current.value = "";
  };

  const stopCaptureStream = useCallback(() => {
    captureStreamRef.current?.getTracks().forEach((t) => t.stop());
    captureStreamRef.current = null;
    const v = captureVideoRef.current;
    if (v) {
      v.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!captureDialogOpen) {
      stopCaptureStream();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera is not available in this browser. Use Upload Photo instead.");
      setCaptureDialogOpen(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        captureStreamRef.current = stream;
        const attach = () => {
          const el = captureVideoRef.current;
          if (!el) return false;
          el.srcObject = stream;
          void el.play().catch(() => {
            /* autoplay policies */
          });
          return true;
        };
        if (!attach()) {
          window.setTimeout(() => {
            if (!cancelled) attach();
          }, 100);
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not access the camera. Allow permission or use Upload Photo.");
          setCaptureDialogOpen(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      stopCaptureStream();
    };
  }, [captureDialogOpen, stopCaptureStream]);

  const takePhotoFromCamera = () => {
    const video = captureVideoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      toast.error("Camera is not ready yet. Wait a moment and try again.");
      return;
    }
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Could not capture image.");
          return;
        }
        if (blob.size > EMPLOYEE_PHOTO_MAX_BYTES) {
          toast.error("Captured image is too large. Try Upload Photo with a smaller file.");
          return;
        }
        const file = new File([blob], `employee-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        void processEmployeePhotoFile(file);
        setCaptureDialogOpen(false);
        stopCaptureStream();
      },
      "image/jpeg",
      0.92,
    );
  };

  const addAllowance = () => {
    setDraft((d) => ({
      ...d,
      allowances: [...d.allowances, { id: crypto.randomUUID(), allowanceTypeId: "", name: "", amount: "" }],
    }));
  };

  const removeAllowance = (id: string) => {
    setDraft((d) => ({ ...d, allowances: d.allowances.filter((a) => a.id !== id) }));
  };

  const patchAllowance = (id: string, patch: Partial<Pick<EmployeeAllowance, "allowanceTypeId" | "name" | "amount">>) => {
    setDraft((d) => ({
      ...d,
      allowances: d.allowances.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    }));
  };

  const addDeduction = () => {
    setDraft((d) => ({
      ...d,
      deductions: [...d.deductions, { id: crypto.randomUUID(), deductionTypeId: "", name: "", amount: "" }],
    }));
  };

  const removeDeduction = (id: string) => {
    setDraft((d) => ({ ...d, deductions: d.deductions.filter((row) => row.id !== id) }));
  };

  const patchDeductionTyped = (
    id: string,
    patch: Partial<Pick<EmployeeDeduction, "deductionTypeId" | "name" | "amount">>,
  ) => {
    setDraft((d) => ({
      ...d,
      deductions: d.deductions.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  };

  const validateBasic = (): boolean => {
    if (!draft.firstName.trim()) {
      toast.error("First name is required.");
      return false;
    }
    if (!draft.lastName.trim()) {
      toast.error("Last name is required.");
      return false;
    }
    const emailTrim = draft.email.trim();
    const phoneTrim = draft.primaryContact.trim();
    if (!phoneTrim) {
      toast.error("Phone number is required.");
      return false;
    }
    if (!draft.cnic.trim()) {
      toast.error("CNIC is required.");
      return false;
    }
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error("Enter a valid email address.");
      return false;
    }
    return true;
  };

  const ensureFullName = () => {
    setDraft((d) => ({
      ...d,
      fullName: `${d.firstName} ${d.lastName}`.trim(),
    }));
  };

  /** Copy present → permanent when permanent lines are empty */
  const copyPresentToPermanentIfEmpty = () => {
    setDraft((d) => {
      if (d.permanentAddressLine1.trim() || d.permanentCity.trim()) return d;
      return {
        ...d,
        permanentAddressLine1: d.presentAddressLine1,
        permanentAddressLine2: d.presentAddressLine2,
        permanentCity: d.presentCity,
        permanentState: d.presentState,
        permanentCountry: d.presentCountry,
      };
    });
  };

  const validateEmployment = (): boolean => {
    if (!isEdit && !draft.employeeCode.trim()) {
      toast.error("Employee code is required.");
      return false;
    }
    if (!draft.departmentId.trim()) {
      toast.error("Department is required.");
      return false;
    }
    if (!draft.designationId.trim()) {
      toast.error("Designation is required.");
      return false;
    }
    if (!draft.employeeType.trim()) {
      toast.error("Employee type is required.");
      return false;
    }
    if (!draft.employeeStatus.trim()) {
      toast.error("Status is required.");
      return false;
    }
    if (!draft.joiningDate.trim()) {
      toast.error("Joining date is required.");
      return false;
    }
    return true;
  };

  const validateSalary = (): boolean => {
    if (grossInvalid(draft.basicSalary)) {
      toast.error("Basic salary must be a valid number.");
      return false;
    }
    for (const a of draft.allowances) {
      const tid = a.allowanceTypeId.trim();
      const t = tid ? getEmployeeAllowanceTypeById(tid) : undefined;
      const label = (t?.label ?? a.name).trim();
      const hasLabel = Boolean(label);
      const digits = a.amount.replace(/\D/g, "");
      const hasAmount = Boolean(digits);
      if (hasLabel && !hasAmount) {
        toast.error(`Enter an amount for ${label}.`);
        return false;
      }
      if (hasAmount && !hasLabel) {
        toast.error("Select an allowance type for each amount (manage types under Allowance types).");
        return false;
      }
      if (hasLabel && allowanceAmountInvalid(a.amount)) {
        toast.error(`Allowance "${label}" amount must be a valid number.`);
        return false;
      }
    }
    for (const row of draft.deductions) {
      const label = row.name.trim();
      const digits = row.amount.replace(/\D/g, "");
      const hasLabel = Boolean(label);
      const hasAmount = Boolean(digits);
      if (hasLabel && !hasAmount) {
        toast.error(`Enter an amount for deduction "${label}".`);
        return false;
      }
      if (hasAmount && !hasLabel) {
        toast.error("Enter a name for each deduction amount.");
        return false;
      }
      if (hasLabel && allowanceAmountInvalid(row.amount)) {
        toast.error(`Deduction "${label}" amount must be a valid number.`);
        return false;
      }
    }
    return true;
  };

  const validateStep = (stepId: (typeof wizardSteps)[number]["id"]): boolean => {
    if (stepId === "basic") {
      if (!validateBasic()) return false;
      ensureFullName();
      return true;
    }
    if (stepId === "employment") {
      if (!validateEmployment()) return false;
      copyPresentToPermanentIfEmpty();
      return true;
    }
    if (stepId === "salary") {
      return validateSalary();
    }
    return true;
  };

  const goToStep = (targetIndex: number) => {
    if (formBlocked || saveLoading) return;
    if (targetIndex === wizardStep) return;
    if (targetIndex < 0 || targetIndex >= stepCount) return;

    if (targetIndex > wizardStep) {
      for (let i = wizardStep; i < targetIndex; i++) {
        const stepId = wizardSteps[i]?.id;
        if (!stepId || !validateStep(stepId)) return;
      }
    }

    setWizardStep(targetIndex);
  };

  const goNext = () => {
    goToStep(wizardStep + 1);
  };

  const goBack = () => {
    goToStep(wizardStep - 1);
  };

  const draftWithComputedFullName = (d: EmployeeDraft): EmployeeDraft => ({
    ...d,
    fullName: `${d.firstName} ${d.lastName}`.trim(),
    allowances: d.allowances
      .map((a) => {
        const type = a.allowanceTypeId.trim() ? getEmployeeAllowanceTypeById(a.allowanceTypeId.trim()) : undefined;
        const name = (type?.label ?? a.name).trim();
        return { ...a, name, amount: a.amount.replace(/[^\d]/g, "") };
      })
      .filter((a) => a.name.trim() && a.amount.replace(/\D/g, "")),
    deductions: d.deductions
      .map((row) => ({ ...row, name: row.name.trim(), amount: row.amount.replace(/[^\d]/g, "") }))
      .filter((row) => row.name.length > 0 && row.amount.replace(/\D/g, "").length > 0),
  });

  const isZktecoSynced = Boolean(draft.deviceUid?.trim());

  const openEmployeeCodeModal = () => {
    setEmployeeCodeDraft(draft.employeeCode);
    setEmployeeCodeModalOpen(true);
  };

  const saveEmployeeCode = async () => {
    if (!editId || employeeCodeSaving) return;
    const code = employeeCodeDraft.trim();
    if (!code) {
      toast.error("Employee code is required.");
      return;
    }
    setEmployeeCodeSaving(true);
    try {
      const result = await updateEmployeeCode(editId, code);
      setDraft((d) => ({ ...d, employeeCode: result.record.employeeCode, deviceUid: result.record.deviceUid }));
      toast.success(result.message);
      setEmployeeCodeModalOpen(false);
    } catch (e) {
      toast.error(employeeApiErrorMessage(e));
    } finally {
      setEmployeeCodeSaving(false);
    }
  };

  const finish = async () => {
    if (!validateBasic() || !validateEmployment() || !validateSalary()) {
      if (!validateBasic()) setWizardStep(0);
      else if (!validateEmployment()) setWizardStep(1);
      else setWizardStep(stepCount - 1);
      return;
    }
    ensureFullName();
    setSaveLoading(true);
    try {
      const payload = draftWithComputedFullName(draft);
      const fileForUpload = employeePhotoFileRef.current ?? employeePhotoFile;
      const photoOpts = fileForUpload ? { photoFile: fileForUpload } : undefined;
      const accessOpts =
        isEdit && editId && draft.userId?.trim() && accessReady
          ? {
              ...photoOpts,
              access: {
                roleNames: Array.from(selectedRoleNames).sort(),
                permissionNames: Array.from(selectedPermNames).sort(),
              },
            }
          : photoOpts;
      if (isEdit && editId) {
        const result = await updateEmployee(editId, payload, accessOpts);
        if (!result) {
          toast.error("Employee not found.");
          return;
        }
        toast.success(result.message);
        navigate(`/employees/${editId}`);
      } else {
        const result = await createEmployee(payload, photoOpts);
        toast.success(result.message);
        navigate("/employees");
      }
    } catch (e) {
      console.error(e);
      toast.error(employeeApiErrorMessage(e));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => navigate("/employees");

  const isLastStep = wizardStep >= stepCount - 1;
  const formBlocked = bootLoading || (isEdit && !editHydrated);
  const pageTitle = isEdit ? "Edit Employee" : "Add Employee";

  return (
    <Layout title={pageTitle}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">{pageTitle}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Step {wizardStep + 1} of {stepCount}.{" "}
                {isEdit ? (
                  <>Update this employee&rsquo;s record.</>
                ) : (
                  <>
                    Layout aligned with{" "}
                    <Link to="/products/add" className="text-primary font-medium hover:underline">
                      Add Product
                    </Link>{" "}
                    wizard style.
                  </>
                )}
              </p>
            </div>
          </div>
          <Button type="button" variant="glass" onClick={handleCancel}>
            Cancel
          </Button>
        </div>

        {!editLoadError ? (
        <div className="overflow-x-auto rounded-xl border border-border/50 bg-card/40 px-3 py-5 sm:px-6">
          <div className="flex min-w-[640px] items-center sm:min-w-0">
            {wizardSteps.map((s, i) => {
              const done = i < wizardStep;
              const active = i === wizardStep;
              const lineComplete = i < wizardStep;
              return (
                <Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => goToStep(i)}
                    disabled={saveLoading || formBlocked}
                    className={cn(
                      "flex shrink-0 items-center gap-2 sm:gap-3 rounded-lg px-1.5 py-1 -mx-0.5 text-left outline-none transition-colors",
                      "hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      "disabled:pointer-events-none disabled:opacity-50",
                    )}
                    aria-label={`Go to ${s.label}`}
                    aria-current={active ? "step" : undefined}
                  >
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors pointer-events-none",
                        done && "bg-[#1a56db] text-white shadow-sm",
                        active && !done && "bg-[#f05252] text-white shadow-sm",
                        !done && !active && "bg-[#f3f4f6] text-[#4b5563]",
                      )}
                    >
                      {done ? (
                        <Check className="h-5 w-5 stroke-[3]" aria-hidden />
                      ) : active ? (
                        <X className="h-5 w-5 stroke-[3]" aria-hidden />
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                    <span
                      className={cn(
                        "max-w-[6.5rem] truncate text-xs font-medium sm:max-w-[11rem] sm:text-sm pointer-events-none",
                        active ? "text-foreground" : done ? "text-foreground/90" : "text-muted-foreground",
                      )}
                      title={s.label}
                    >
                      {s.label}
                    </span>
                  </button>
                  {i < wizardSteps.length - 1 ? (
                    <div
                      className={cn(
                        "mx-2 h-0.5 min-w-[1.25rem] flex-1 rounded-full sm:mx-3",
                        lineComplete ? "bg-[#1a56db]" : "bg-[#e5e7eb]",
                      )}
                      aria-hidden
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </div>
        ) : null}

        {editLoadError ? (
          <div className="glass-card p-8 space-y-4">
            <p className="text-sm text-muted-foreground">{editLoadError}</p>
            <Button variant="glass" asChild>
              <Link to="/employees">Back to employees</Link>
            </Button>
          </div>
        ) : formBlocked ? (
          <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {bootLoading ? "Loading departments and designations…" : "Loading employee…"}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {currentStepId === "basic" ? (
              <motion.div
                key="basic"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="space-y-6"
              >
                <div className="glass-card p-6">
                  <SectionHeader title="Employee Photo" icon={Camera} />
                  <input
                    ref={photoUploadInputRef}
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    className="sr-only"
                    onChange={(e) => {
                      void processEmployeePhotoFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="relative shrink-0 mx-auto sm:mx-0">
                      <div
                        className={cn(
                          "flex h-[100px] w-[100px] sm:h-[120px] sm:w-[120px] items-center justify-center rounded-full border-2 border-dashed border-border/60 bg-muted/20 overflow-hidden",
                          employeePhotoPreview && "border-solid border-border/40",
                        )}
                      >
                        {employeePhotoPreview ? (
                          <img src={employeePhotoPreview} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-12 w-12 sm:h-14 sm:w-14 text-muted-foreground/35" strokeWidth={1.25} />
                        )}
                      </div>
                      {employeePhotoPreview ? (
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          className="absolute -top-1 -right-1 h-7 w-7 rounded-full shadow-md border border-border"
                          onClick={clearEmployeePhoto}
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-3 min-w-0 flex-1 items-center sm:items-start">
                      <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          className="gap-2 h-10"
                          onClick={() => photoUploadInputRef.current?.click()}
                        >
                          <Upload className="h-4 w-4" />
                          Upload Photo
                        </Button>
                        <Button type="button" variant="secondary" className="gap-2 h-10" onClick={() => setCaptureDialogOpen(true)}>
                          <Camera className="h-4 w-4" />
                          Capture
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground text-center sm:text-left">
                        JPG, PNG up to 5MB. Recommended 400×400px.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="glass-card p-6">
                <SectionHeader title="Basic Information" subtitle="Identity, contact, and addresses" icon={User} />

                <div className="space-y-6">
                  <FieldGroup title="Name">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          First Name <span className="text-destructive">*</span>
                        </Label>
                        <Input className="glass-input h-10" value={draft.firstName} onChange={(e) => update("firstName", e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Last Name <span className="text-destructive">*</span>
                        </Label>
                        <Input className="glass-input h-10" value={draft.lastName} onChange={(e) => update("lastName", e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Father / Husband Name</Label>
                        <Input
                          className="glass-input h-10"
                          value={draft.fatherHusbandName}
                          onChange={(e) => update("fatherHusbandName", e.target.value)}
                        />
                      </div>
                    </div>
                  </FieldGroup>

                  <FieldGroup title="Identity & contact">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          CNIC <span className="text-destructive">*</span>
                        </Label>
                        <Input className="glass-input h-10" value={draft.cnic} onChange={(e) => update("cnic", mask ? formatWithMask(e.target.value, mask) : e.target.value)} placeholder={placeholder} maxLength={maxLength} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Phone No <span className="text-destructive">*</span>
                        </Label>
                        <Input className="glass-input h-10" value={draft.primaryContact} onChange={(e) => update("primaryContact", e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <Input className="glass-input h-10" type="email" value={draft.email} onChange={(e) => update("email", e.target.value)} placeholder="Optional" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Date of birth</Label>
                        <DatePartsPicker
                          value={draft.dateOfBirth}
                          onChange={(iso) => update("dateOfBirth", iso)}
                          fromYear={1940}
                          toYear={new Date().getFullYear()}
                          maxDate={new Date()}
                          placeholder="Select date of birth"
                        />
                      </div>
                    </div>
                  </FieldGroup>

                  <FieldGroup title="Present Address">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Address line 1</Label>
                        <Input className="glass-input h-10" value={draft.presentAddressLine1} onChange={(e) => update("presentAddressLine1", e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Address line 2</Label>
                        <Input className="glass-input h-10" value={draft.presentAddressLine2} onChange={(e) => update("presentAddressLine2", e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">City</Label>
                        <Input className="glass-input h-10" value={draft.presentCity} onChange={(e) => update("presentCity", e.target.value)} />
                      </div>
                      <PakistanProvinceSelect label="State" value={draft.presentState} onChange={(v) => update("presentState", v)} />
                      <PakistanCountrySelect label="Country" value={draft.presentCountry} onChange={(v) => update("presentCountry", v)} />
                    </div>
                  </FieldGroup>

                  <FieldGroup title="Permanent Address">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Address line 1</Label>
                        <Input
                          className="glass-input h-10"
                          value={draft.permanentAddressLine1}
                          onChange={(e) => update("permanentAddressLine1", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Address line 2</Label>
                        <Input
                          className="glass-input h-10"
                          value={draft.permanentAddressLine2}
                          onChange={(e) => update("permanentAddressLine2", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">City</Label>
                        <Input className="glass-input h-10" value={draft.permanentCity} onChange={(e) => update("permanentCity", e.target.value)} />
                      </div>
                      <PakistanProvinceSelect label="State" value={draft.permanentState} onChange={(v) => update("permanentState", v)} />
                      <PakistanCountrySelect label="Country" value={draft.permanentCountry} onChange={(v) => update("permanentCountry", v)} />
                    </div>
                  </FieldGroup>
                </div>
                </div>
              </motion.div>
            ) : currentStepId === "employment" ? (
              <motion.div
                key="employment"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="glass-card p-6"
              >
                <SectionHeader title="Employment Information" subtitle="Department, role, and tenure" icon={Building2} />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <div className="space-y-2 xl:col-span-3">
                    <Label className="text-xs text-muted-foreground">
                      Employee Code
                      {!isEdit ? <span className="text-destructive"> *</span> : null}
                    </Label>
                    <div className="flex items-center gap-2 max-w-xs">
                      <Input
                        className={cn(
                          "glass-input h-10 font-mono text-sm flex-1",
                          isEdit && "bg-muted/50",
                        )}
                        readOnly={isEdit}
                        value={draft.employeeCode}
                        onChange={isEdit ? undefined : (e) => update("employeeCode", e.target.value)}
                        placeholder={isEdit ? undefined : "e.g. EMP-00001"}
                      />
                      {isEdit && canEdit && !isZktecoSynced ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={openEmployeeCodeModal}
                          aria-label="Edit employee code"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      ) : null}
                    </div>
                    {!isEdit ? (
                      <p className="text-xs text-muted-foreground">
                        A code is suggested automatically — you can change it before saving.
                      </p>
                    ) : isZktecoSynced ? (
                      <p className="text-xs text-muted-foreground">
                        Enrolled on biometric device — employee code cannot be changed here.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Not synced with ZKTeco — use the edit button to update the employee code.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Department <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={draft.departmentId || "__none__"}
                      onValueChange={(v) => {
                        const id = v === "__none__" ? "" : v;
                        const dep = departments.find((x) => x.id === id);
                        setDraft((d) => ({
                          ...d,
                          departmentId: id,
                          departmentName: dep?.name ?? "",
                        }));
                      }}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select…</SelectItem>
                        {departments.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Designation <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={draft.designationId || "__none__"}
                      onValueChange={(v) => {
                        const id = v === "__none__" ? "" : v;
                        const des = designations.find((x) => x.id === id);
                        setDraft((d) => ({
                          ...d,
                          designationId: id,
                          designationName: des?.name ?? "",
                        }));
                      }}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Select designation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select…</SelectItem>
                        {designations.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Employee type <span className="text-destructive">*</span>
                    </Label>
                    <Select value={draft.employeeType || "__none__"} onValueChange={(v) => update("employeeType", v === "__none__" ? "" : v)}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select…</SelectItem>
                        {EMPLOYEE_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Payroll model <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={draft.payrollModel || "monthly"}
                      onValueChange={(v) => update("payrollModel", v === "hourly" ? "hourly" : "monthly")}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly (fixed)</SelectItem>
                        <SelectItem value="hourly">Hourly (worked hours)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Status <span className="text-destructive">*</span>
                    </Label>
                    <Select value={draft.employeeStatus || "__none__"} onValueChange={(v) => update("employeeStatus", v === "__none__" ? "" : v)}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select…</SelectItem>
                        {EMPLOYEE_STATUSES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Confirmation Date</Label>
                    <DatePartsPicker
                      value={draft.confirmationDate}
                      onChange={(iso) => update("confirmationDate", iso)}
                      fromYear={1980}
                      toYear={new Date().getFullYear() + 1}
                      placeholder="Select confirmation date"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Joining Date <span className="text-destructive">*</span>
                    </Label>
                    <DatePartsPicker
                      value={draft.joiningDate}
                      onChange={(iso) => update("joiningDate", iso)}
                      fromYear={1980}
                      toYear={new Date().getFullYear() + 1}
                      placeholder="Select joining date"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2 xl:col-span-3">
                    <Label className="text-xs text-muted-foreground">Reporting To</Label>
                    <ReportingToPicker
                      value={draft.reportingTo.trim()}
                      onChange={(v) => update("reportingTo", v)}
                      employees={reportingToOptions}
                      orphanLabel={reportingToOrphan}
                      placeholder="Search manager by name, code, department, or designation…"
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-6">
                  Reference data:{" "}
                  <Link to="/departments" className="text-primary font-medium hover:underline">
                    Departments
                  </Link>
                  ,{" "}
                  <Link to="/designations" className="text-primary font-medium hover:underline">
                    Designations
                  </Link>
                  ,{" "}
                  <Link to="/employee-allowance-types" className="text-primary font-medium hover:underline">
                    Allowance types
                  </Link>
                  .
                </p>
              </motion.div>
            ) : currentStepId === "accessibility" ? (
              <motion.div
                key="accessibility"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="glass-card p-6 space-y-6"
              >
                <SectionHeader
                  title="Accessibility"
                  subtitle="Roles and direct permissions for this employee’s linked login account (same as User access)."
                  icon={Accessibility}
                />
                <p className="text-xs text-muted-foreground">
                  Manage catalogs under{" "}
                  <Link to="/roles" className="text-primary font-medium hover:underline">
                    Roles
                  </Link>{" "}
                  and{" "}
                  <Link to="/permissions" className="text-primary font-medium hover:underline">
                    Permissions
                  </Link>
                  .
                </p>
                <SpatieAccessEditor
                  loading={accessCatalogLoading}
                  roleCatalog={roleCatalog}
                  permCatalog={permCatalog}
                  selectedRoleNames={selectedRoleNames}
                  setSelectedRoleNames={setSelectedRoleNames}
                  selectedPermNames={selectedPermNames}
                  setSelectedPermNames={setSelectedPermNames}
                />
              </motion.div>
            ) : currentStepId === "salary" ? (
              <motion.div
                key="salary"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="glass-card p-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6 min-w-0">
                    <SectionHeader
                      title="Salary Information"
                      subtitle="Basic salary, allowances, deductions, and bank details"
                      icon={Wallet}
                    />
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Basic Salary</Label>
                      <Input
                        className={cn("glass-input h-10 max-w-md", grossInvalid(draft.basicSalary) && "border-destructive")}
                        inputMode="numeric"
                        placeholder="e.g. 150,000"
                        value={formatGrossDisplay(draft.basicSalary)}
                        onChange={(e) => update("basicSalary", e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Allowances</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Types are maintained separately (like purchase cost types).{" "}
                          <Link to="/employee-allowance-types" className="text-primary font-medium hover:underline">
                            Manage allowance types
                          </Link>
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="glass" className="h-8 gap-1 text-xs shrink-0" onClick={addAllowance}>
                        <Plus className="w-3.5 h-3.5" />
                        Add line
                      </Button>
                    </div>
                    {draft.allowances.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No allowance lines — add one to assign amounts per type.</p>
                    ) : (
                      <div className="space-y-2">
                        {draft.allowances.map((row) => {
                          const sel = row.allowanceTypeId.trim() || ALLOWANCE_TYPE_NONE;
                          const resolvedLabel = row.allowanceTypeId.trim()
                            ? getEmployeeAllowanceTypeById(row.allowanceTypeId.trim())?.label
                            : "";
                          const showInvalidAmount =
                            Boolean(resolvedLabel || row.name.trim()) && allowanceAmountInvalid(row.amount);
                          return (
                            <div key={row.id} className="flex flex-col sm:flex-row gap-2 sm:items-end">
                              <div className="space-y-1 flex-1 min-w-0">
                                <Label className="text-[10px] text-muted-foreground">Allowance type</Label>
                                <Select
                                  value={sel}
                                  onValueChange={(v) => {
                                    const id = v === ALLOWANCE_TYPE_NONE ? "" : v;
                                    const t = id ? getEmployeeAllowanceTypeById(id) : undefined;
                                    patchAllowance(row.id, { allowanceTypeId: id, name: t?.label ?? "" });
                                  }}
                                >
                                  <SelectTrigger className="glass-input h-9 text-sm">
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={ALLOWANCE_TYPE_NONE}>Select…</SelectItem>
                                    {allowanceTypes.map((t) => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1 w-full sm:w-40 shrink-0">
                                <Label className="text-[10px] text-muted-foreground">Amount</Label>
                                <Input
                                  className={cn("glass-input h-9 text-sm", showInvalidAmount && "border-destructive")}
                                  inputMode="numeric"
                                  placeholder="0"
                                  value={formatGrossDisplay(row.amount)}
                                  onChange={(e) => patchAllowance(row.id, { amount: e.target.value.replace(/[^\d]/g, "") })}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive shrink-0"
                                onClick={() => removeAllowance(row.id)}
                                aria-label="Remove allowance line"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deductions</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Select a deduction type (PF, tax, etc.) and enter amount per line.{" "}
                          <Link to="/employee-deduction-types" className="text-primary font-medium hover:underline">
                            Manage deduction types
                          </Link>
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="glass" className="h-8 gap-1 text-xs shrink-0" onClick={addDeduction}>
                        <Plus className="w-3.5 h-3.5" />
                        Add line
                      </Button>
                    </div>
                    {draft.deductions.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No deduction lines — add one if applicable.</p>
                    ) : (
                      <div className="space-y-2">
                        {draft.deductions.map((row) => {
                          const sel = row.deductionTypeId?.trim() || DEDUCTION_TYPE_NONE;
                          const resolvedLabel = row.deductionTypeId?.trim()
                            ? getEmployeeDeductionTypeById(row.deductionTypeId.trim())?.label
                            : "";
                          const showInvalidAmount =
                            Boolean(resolvedLabel || row.name.trim()) && allowanceAmountInvalid(row.amount);
                          return (
                            <div key={row.id} className="flex flex-col sm:flex-row gap-2 sm:items-end">
                              <div className="space-y-1 flex-1 min-w-0">
                                <Label className="text-[10px] text-muted-foreground">Deduction type</Label>
                                <Select
                                  value={sel}
                                  onValueChange={(v) => {
                                    const id = v === DEDUCTION_TYPE_NONE ? "" : v;
                                    const t = id ? getEmployeeDeductionTypeById(id) : undefined;
                                    patchDeductionTyped(row.id, { deductionTypeId: id, name: t?.label ?? "" });
                                  }}
                                >
                                  <SelectTrigger className="glass-input h-9 text-sm">
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={DEDUCTION_TYPE_NONE}>Select…</SelectItem>
                                    {deductionTypes.map((t) => (
                                      <SelectItem key={t.id} value={String(t.id)}>
                                        {t.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1 w-full sm:w-40 shrink-0">
                                <Label className="text-[10px] text-muted-foreground">Amount</Label>
                                <Input
                                  className={cn("glass-input h-9 text-sm", showInvalidAmount && "border-destructive")}
                                  inputMode="numeric"
                                  placeholder="0"
                                  value={formatGrossDisplay(row.amount)}
                                  onChange={(e) => patchDeductionTyped(row.id, { amount: e.target.value.replace(/[^\d]/g, "") })}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive shrink-0"
                                onClick={() => removeDeduction(row.id)}
                                aria-label="Remove deduction line"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Bank Name</Label>
                      <Input className="glass-input h-10" value={draft.bankName} onChange={(e) => update("bankName", e.target.value)} placeholder="e.g. UBL" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Account Number</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={draft.bankAccountNumber}
                        onChange={(e) => update("bankAccountNumber", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-xs text-muted-foreground">Account Holder Name</Label>
                      <Input className="glass-input h-10" value={draft.accountHolderName} onChange={(e) => update("accountHolderName", e.target.value)} />
                    </div>
                  </div>
                  </div>

                  <div className="space-y-4 self-start">
                    <SectionHeader
                      title="Leave policy"
                      subtitle="Assign a leave policy for the active leave period."
                      icon={ScrollText}
                    />

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Leave policy</h4>
                      <p className="text-[11px] text-muted-foreground mt-1">Assign a leave policy for the active leave period.</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Leave period</Label>
                      <Input
                        className="glass-input h-10"
                        value={
                          activeLeaveYear
                            ? `${activeLeaveYear.label} (${activeLeaveYear.startDate} → ${activeLeaveYear.endDate})`
                            : "No active leave period"
                        }
                        readOnly
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Policy</Label>
                      <Select
                        value={draft.leavePolicyId || "__none__"}
                        onValueChange={(v) => {
                          const id = v === "__none__" ? "" : v;
                          update("leavePolicyId", id);
                          update("leaveYearId", activeLeaveYear?.id ? String(activeLeaveYear.id) : "");
                        }}
                        disabled={!activeLeaveYear}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder={activeLeaveYear ? "Select policy" : "No active leave period"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">None</SelectItem>
                          {leavePolicies.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        This will create leave allocations for all leave types inside the selected policy.
                      </p>
                    </div>
                    </div>

                    <SectionHeader
                      title="Work shift"
                      subtitle="Assign a work shift for attendance and scheduling."
                      icon={Building2}
                    />

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Work shift</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">Select the current work shift for this employee.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Shift</Label>
                        <Select
                          value={draft.workShiftId || "__none__"}
                          onValueChange={(v) => update("workShiftId", v === "__none__" ? "" : v)}
                        >
                          <SelectTrigger className="glass-input h-10">
                            <SelectValue placeholder="Select work shift" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {workShifts
                              .filter((s) => s.active)
                              .slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((s) => (
                                <SelectItem key={s.id} value={String(s.id)}>
                                  {s.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">
                          Shifts are managed under{" "}
                          <Link to="/work-shifts" className="text-primary font-medium hover:underline">
                            Work shifts
                          </Link>
                          .
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Off days</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Select weekly off days for this employee. These are applied automatically when filling the roster.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {EMPLOYEE_OFF_DAY_OPTIONS.map((d) => {
                            const active = draft.employeeOffDays.includes(d.id);
                            return (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() =>
                                  update("employeeOffDays", toggleEmployeeOffDay(draft.employeeOffDays, d.id))
                                }
                                className={cn(
                                  "h-9 min-w-[2.75rem] px-2.5 rounded-lg border text-xs font-semibold transition-colors",
                                  active
                                    ? "border-primary/25 bg-primary/10 text-foreground"
                                    : "border-border/60 bg-background/60 text-muted-foreground hover:bg-muted/30",
                                )}
                              >
                                {d.short}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {draft.employeeOffDays.length
                            ? `Selected: ${draft.employeeOffDays
                                .map((code) => EMPLOYEE_OFF_DAY_OPTIONS.find((d) => d.id === code)?.short ?? code)
                                .join(", ")}`
                            : "No weekly off days selected."}
                        </p>
                      </div>

                      {selectedWorkShift ? (
                        <div className="rounded-lg border border-border/50 bg-background/40 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Current work shift
                              </p>
                              <p className="text-sm font-semibold text-foreground mt-1 truncate" title={selectedWorkShift.name}>
                                {selectedWorkShift.name || "—"}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatTimeDisplayAmPm(selectedWorkShift.startTime)} – {formatTimeDisplayAmPm(selectedWorkShift.endTime)}
                                {selectedWorkShift.crossesMidnight ? " (+1)" : ""} · {selectedWorkShift.breakMinutes}m break
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {selectedWorkShift.crossesMidnight ? (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/25 bg-violet-500/10 text-violet-800 dark:text-violet-300 font-semibold">
                                  Night
                                </span>
                              ) : null}
                              <span
                                className={cn(
                                  "text-[10px] px-2 py-0.5 rounded-full border font-semibold",
                                  selectedWorkShift.active
                                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
                                    : "border-border/50 bg-muted/40 text-muted-foreground",
                                )}
                              >
                                {selectedWorkShift.active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>
                          {selectedWorkShift.slug ? (
                            <p className="mt-2 text-[11px] text-muted-foreground font-mono truncate" title={selectedWorkShift.slug}>
                              {selectedWorkShift.slug}
                            </p>
                          ) : null}
                        </div>
                      ) : null}

                      {draft.workShiftAssignments?.length ? (
                        <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Work shift history
                          </p>
                          <ul className="mt-2 space-y-2">
                            {draft.workShiftAssignments
                              .slice()
                              .sort((a, b) => (b.effectiveFrom || "").localeCompare(a.effectiveFrom || ""))
                              .slice(0, 8)
                              .map((a) => {
                                const label = a.workShiftName?.trim() ? a.workShiftName : a.workShiftId;
                                const range = `${a.effectiveFrom || "—"} → ${a.effectiveTo || "Present"}`;
                                const isCurrent = !a.effectiveTo;
                                return (
                                  <li
                                    key={`${a.workShiftId}-${a.effectiveFrom}-${a.effectiveTo}`}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/50 bg-background/40 px-3 py-2 text-sm"
                                  >
                                    <span className="min-w-0">
                                      <span className="font-medium text-foreground">{label}</span>
                                      <span className="text-xs text-muted-foreground">{" "}· {range}</span>
                                    </span>
                                    {isCurrent ? (
                                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold">
                                        Current
                                      </span>
                                    ) : null}
                                  </li>
                                );
                              })}
                          </ul>
                        </div>
                      ) : null}
                    </div>

                    <SectionHeader
                      title="Work policy"
                      subtitle="Optional policy that bundles attendance rules for this employee."
                      icon={FileCheck2}
                    />

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Work policy</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Only active policies with at least one attendance rule are listed. Manage policies under{" "}
                          <Link to="/work-policies" className="text-primary font-medium hover:underline">
                            Work policies
                          </Link>
                          .
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Policy</Label>
                        <Select
                          value={draft.workPolicyId || "__none__"}
                          onValueChange={(v) => {
                            const id = v === "__none__" ? "" : v;
                            setDraft((d) => {
                              if (v === "__none__") {
                                return { ...d, workPolicyId: "", workPolicyName: "" };
                              }
                              const hit = workPolicySelectOptions.find((p) => String(p.id) === id);
                              const name =
                                hit && !hit.name.includes("not assignable") ? hit.name : d.workPolicyName;
                              return { ...d, workPolicyId: id, workPolicyName: name };
                            });
                          }}
                        >
                          <SelectTrigger className="glass-input h-10">
                            <SelectValue placeholder="No work policy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {workPolicySelectOptions.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <SectionHeader
                      title="Salary policy"
                      subtitle="Optional policy that bundles salary calculation rules for payroll."
                      icon={Banknote}
                    />

                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-3">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Salary policy</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Only active policies with at least one salary rule are listed. Manage policies under{" "}
                          <Link to="/salary-policies" className="text-primary font-medium hover:underline">
                            Salary policies
                          </Link>
                          .
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Policy</Label>
                        <Select
                          value={draft.salaryPolicyId || "__none__"}
                          onValueChange={(v) => {
                            const id = v === "__none__" ? "" : v;
                            setDraft((d) => {
                              if (v === "__none__") {
                                return { ...d, salaryPolicyId: "", salaryPolicyName: "" };
                              }
                              const hit = salaryPolicySelectOptions.find((p) => String(p.id) === id);
                              const name =
                                hit && !hit.name.includes("not assignable") ? hit.name : d.salaryPolicyName;
                              return { ...d, salaryPolicyId: id, salaryPolicyName: name };
                            });
                          }}
                        >
                          <SelectTrigger className="glass-input h-10">
                            <SelectValue placeholder="No salary policy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {salaryPolicySelectOptions.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}

        {!editLoadError ? (
          <div className="flex flex-col-reverse gap-3 border-t border-border/40 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Button type="button" variant="glass" className="w-full gap-2 sm:w-auto" disabled={wizardStep === 0 || saveLoading || formBlocked} onClick={goBack}>
              Back
            </Button>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
              {isLastStep ? (
                <Button
                  type="button"
                  variant="premium"
                  className="w-full gap-2 px-8 sm:w-auto"
                  disabled={saveLoading || formBlocked}
                  onClick={() => void finish()}
                >
                  {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {isEdit ? "Save changes" : "Save Employee"}
                </Button>
              ) : (
                <Button type="button" variant="premium" className="w-full gap-2 px-8 sm:w-auto" disabled={saveLoading || formBlocked} onClick={goNext}>
                  Save &amp; Next
                </Button>
              )}
            </div>
          </div>
        ) : null}

        <Dialog
          open={employeeCodeModalOpen}
          onOpenChange={(open) => {
            if (employeeCodeSaving) return;
            if (!open) setEmployeeCodeModalOpen(false);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit employee code</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label className="text-xs text-muted-foreground">Employee Code</Label>
              <Input
                className="glass-input h-10 font-mono text-sm"
                value={employeeCodeDraft}
                onChange={(e) => setEmployeeCodeDraft(e.target.value)}
                placeholder="e.g. EMP-001"
                autoFocus
                disabled={employeeCodeSaving}
              />
              {!isZktecoSynced ? (
                <p className="text-xs text-muted-foreground">
                  This updates the employee code in the system only.
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="glass" disabled={employeeCodeSaving} onClick={() => setEmployeeCodeModalOpen(false)}>
                Cancel
              </Button>
              <Button type="button" variant="premium" className="gap-2" disabled={employeeCodeSaving} onClick={() => void saveEmployeeCode()}>
                {employeeCodeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={captureDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              stopCaptureStream();
              setCaptureDialogOpen(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Capture photo</DialogTitle>
            </DialogHeader>
            <div className="mx-auto aspect-square w-full max-w-[min(100%,420px)] overflow-hidden rounded-xl bg-black">
              <video ref={captureVideoRef} className="h-full w-full object-cover" autoPlay playsInline muted />
            </div>
            <p className="text-xs text-muted-foreground">Allow camera access, then tap Take photo when you are in frame.</p>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="glass"
                className="w-full sm:w-auto"
                onClick={() => {
                  stopCaptureStream();
                  setCaptureDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="button" variant="premium" className="w-full gap-2 sm:w-auto" onClick={takePhotoFromCamera}>
                <Camera className="h-4 w-4" />
                Take photo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AddEmployee;
